import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { TAG_PRESETS, TagChip } from "@/lib/tags";
import {
  LogOut, Radio, Film, AlertTriangle, Users, Trash2, Plus, Mail, Download, FileText,
  Sliders, ArrowUp, ArrowDown, RotateCcw, Video, Zap, Copy, Check, ExternalLink,
  Rocket, Music, Award, Newspaper, MessageSquare, Facebook, Twitter, Tv, Share2, BookOpen, Youtube, Sparkles,
} from "lucide-react";
import { CutInTab } from "@/components/admin/CutInTab";
import { YouTubeMetaCard } from "@/components/admin/YouTubeMetaCard";
import { SocialStudioCard } from "@/components/admin/SocialStudioCard";
import { AfdArticlesCard } from "@/components/admin/AfdArticlesCard";
import { ForecastStudio } from "@/components/admin/ForecastStudio";
import { ShareGraphicsTab } from "@/components/admin/ShareGraphics";
import { HistoryTab } from "@/components/admin/HistoryTab";
import { ImpactPostCard } from "@/components/admin/ImpactPostCard";
import { CloudRelayGuide, StreamHealthWidget } from "@/components/admin/CloudRelay";
import { CloudEncoderCard } from "@/components/admin/CloudEncoderCard";
import { MusicCard } from "@/components/admin/MusicCard";
import { BroadcastFxCard } from "@/components/admin/BroadcastFxCard";
import { PromoCard } from "@/components/admin/PromoCard";
import { VoiceCard } from "@/components/admin/VoiceCard";
import { WatchCard } from "@/components/admin/WatchCard";

const TABS = [
  { id: "stream", label: "24/7 Stream", icon: Radio, testId: ADMIN.tabStream },
  { id: "bumper", label: "On-Air Bumper", icon: Zap, testId: "admin-tab-bumper" },
  { id: "streaming-setup", label: "Streaming Setup", icon: Rocket, testId: "admin-tab-streaming-setup" },
  { id: "slides", label: "Broadcast Slides", icon: Sliders, testId: "admin-tab-slides" },
  { id: "cameras", label: "Live Cameras", icon: Video, testId: "admin-tab-cameras" },
  { id: "videos", label: "Forecast Videos", icon: Film, testId: ADMIN.tabVideos },
  { id: "studio", label: "Forecast Studio", icon: Tv, testId: "admin-tab-studio" },
  { id: "articles", label: "Forecast Articles", icon: Newspaper, testId: "admin-tab-articles" },
  { id: "history", label: "On This Day", icon: BookOpen, testId: "admin-tab-history" },
  { id: "alerts", label: "Custom Alerts", icon: AlertTriangle, testId: ADMIN.tabAlerts },
  { id: "ticker", label: "News Desk Ticker", icon: MessageSquare, testId: "admin-tab-ticker" },
  { id: "facebook", label: "Facebook Auto-Post", icon: Facebook, testId: "admin-tab-facebook" },
  { id: "twitter", label: "X / Twitter Auto-Post", icon: Twitter, testId: "admin-tab-twitter" },
  { id: "share", label: "Share Graphics", icon: Share2, testId: "admin-tab-share" },
  { id: "cutin", label: "Broadcast Cut-In", icon: Tv, testId: "admin-tab-cutin" },
  { id: "yt-meta", label: "Auto Title & Thumbnail", icon: Youtube, testId: "admin-tab-yt-meta" },
  { id: "social-studio", label: "Pattern Watch & Social", icon: Sparkles, testId: "admin-tab-social-studio" },
  { id: "afd-articles", label: "Auto Forecast Articles", icon: Newspaper, testId: "admin-tab-afd-articles" },
  { id: "team", label: "Team", icon: Users, testId: ADMIN.tabTeam },
  { id: "sponsors", label: "Sponsors", icon: Award, testId: "admin-tab-sponsors" },
  { id: "subscribers", label: "Subscribers", icon: Mail, testId: "admin-tab-subscribers" },
];

const Label = ({ children }) => (
  <label className="mb-1 block font-oswald text-[11px] uppercase tracking-widest text-gray-400">{children}</label>
);
const Input = (props) => (
  <input
    {...props}
    className={`w-full rounded-sm border border-white/15 bg-black/60 px-3 py-2 text-sm text-white outline-none focus:border-[#1f63c9] ${props.className || ""}`}
  />
);
const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full rounded-sm border border-white/15 bg-black/60 px-3 py-2 text-sm text-white outline-none focus:border-[#1f63c9] ${props.className || ""}`}
  />
);
const Btn = ({ children, className = "", ...rest }) => (
  <button
    {...rest}
    className={`inline-flex items-center gap-2 rounded-sm bg-[#1f63c9] px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#2a74d8] disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

function StreamTab() {
  const [val, setVal] = useState("");
  const [channel, setChannel] = useState("");
  const [current, setCurrent] = useState("");
  const [handle, setHandle] = useState("");
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState(null);

  useEffect(() => {
    api.get("/stream").then((r) => {
      setVal(r.data.youtube_video_id || "");
      setChannel(r.data.channel_url || "");
      setCurrent(r.data.youtube_video_id || "");
    });
  }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/stream", { youtube_video_id: val, channel_url: channel });
      setCurrent(data.youtube_video_id);
      setVal(data.youtube_video_id);
      toast.success("Stream updated");
    } catch (e) {
      toast.error("Failed to update stream");
    }
  };

  const findLive = async () => {
    if (!handle.trim()) return toast.error("Enter a channel handle like @georgiaweathernetwork");
    setFinding(true); setFound(null);
    try {
      const { data } = await api.post("/stream/resolve-live", { handle_or_url: handle });
      setFound(data);
      setVal(data.video_id);
      if (data.channel_url) setChannel(data.channel_url);
      if (data.is_live) toast.success("Live stream detected!");
      else toast.warning("Video found, but it may not be live right now.");
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not detect a live stream.";
      toast.error(typeof msg === "string" ? msg : "Detection failed");
    } finally {
      setFinding(false);
    }
  };

  const useFoundAndSave = async () => {
    if (!found) return;
    try {
      const { data } = await api.put("/stream", {
        youtube_video_id: found.video_id,
        channel_url: found.channel_url || channel,
      });
      setCurrent(data.youtube_video_id);
      setVal(data.youtube_video_id);
      setChannel(data.channel_url);
      toast.success("Live stream saved to the home page");
    } catch {
      toast.error("Failed to save");
    }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Auto‑Detect Live Stream
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Paste a YouTube channel handle (e.g. <span className="font-mono text-gray-300">@georgiaweathernetwork</span>)
          or channel URL. We'll find the currently live video automatically — no API key needed.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
          <Input
            data-testid="admin-handle-input"
            placeholder="@georgiaweathernetwork  or  https://youtube.com/@yourchannel"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && findLive()}
          />
          <Btn data-testid="admin-find-live" onClick={findLive} disabled={finding}>
            {finding ? "Searching…" : "Find Live Stream"}
          </Btn>
        </div>

        {found && (
          <div
            data-testid="admin-find-live-result"
            className="mt-4 flex flex-col gap-4 rounded border border-white/10 bg-black/40 p-4 md:flex-row"
          >
            <div className="aspect-video w-full overflow-hidden rounded md:w-64">
              <img
                src={`https://img.youtube.com/vi/${found.video_id}/hqdefault.jpg`}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                  found.is_live ? "bg-[#1f63c9] text-white" : "bg-white/10 text-gray-300"
                }`}>
                  <span className={found.is_live ? "live-dot" : "h-2 w-2 rounded-full bg-gray-400"} />
                  {found.is_live ? "Live Now" : "Not Live"}
                </span>
                <span className="font-mono text-[10px] text-gray-400">ID: {found.video_id}</span>
              </div>
              {found.title && (
                <div className="mt-2 font-oswald text-base uppercase tracking-wider text-white">{found.title}</div>
              )}
              <div className="mt-1 truncate text-xs text-gray-400">{found.resolved_url}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Btn data-testid="admin-use-found" onClick={useFoundAndSave}>Use This & Save</Btn>
                <a
                  href={`https://www.youtube.com/watch?v=${found.video_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-white/40"
                >
                  Open on YouTube
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="glass rounded-sm p-6">
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Manual: 24/7 YouTube Stream</div>
        <p className="mt-1 text-sm text-gray-400">
          Or paste any YouTube live URL / video ID directly. The main page hero embeds this stream.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>YouTube URL or Video ID</Label>
            <Input
              data-testid={ADMIN.streamInput}
              placeholder="https://www.youtube.com/watch?v=... or video ID"
              value={val}
              onChange={(e) => setVal(e.target.value)}
            />
          </div>
          <div>
            <Label>Channel URL (optional)</Label>
            <Input
              placeholder="https://youtube.com/@yourchannel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Btn data-testid={ADMIN.streamSave} onClick={save}>Save Stream</Btn>
          {current && (
            <span className="font-mono text-xs text-gray-400">
              Currently embedded ID: <span className="text-white">{current}</span>
            </span>
          )}
        </div>

        {current && (
          <div className="mt-6 aspect-video overflow-hidden rounded border border-white/10">
            <iframe
              src={`https://www.youtube.com/embed/${current}`}
              title="Preview"
              className="h-full w-full"
              allow="autoplay; encrypted-media"
            />
          </div>
        )}
      </div>

      <ObsBrowserSourceCard />
    </div>
  );
}

function ObsBrowserSourceCard() {
  const [copied, setCopied] = useState("");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const urls = [
    { label: "Broadcast (with chrome)", url: `${origin}/broadcast` },
    { label: "Green‑screen ready (no chrome)", url: `${origin}/broadcast?chrome=off` },
    { label: "Chroma‑key green background", url: `${origin}/broadcast?chrome=off&bg=green` },
    { label: "Slower 12s slides", url: `${origin}/broadcast?chrome=off&duration=12` },
  ];
  const copy = async (u) => {
    try { await navigator.clipboard.writeText(u); setCopied(u); setTimeout(() => setCopied(""), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="glass rounded-sm p-6">
      <div className="flex items-center gap-2">
        <Radio className="h-5 w-5 text-[#1f63c9]" />
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
          Broadcast Graphics · OBS Setup
        </div>
      </div>
      <p className="mt-1 text-sm text-gray-400">
        Feed the auto‑rotating GAWX graphics straight into your 24/7 stream. Add a Browser Source in OBS at 1920×1080 with one of these URLs — the loop auto‑plays and refreshes weather data every 10 minutes.
      </p>
      <div className="mt-4 space-y-2">
        {urls.map((u) => (
          <div key={u.url} className="flex items-center gap-3 rounded border border-white/10 bg-black/40 p-3">
            <div className="min-w-0 flex-1">
              <div className="font-oswald text-xs uppercase tracking-widest text-white">{u.label}</div>
              <div className="truncate font-mono text-[11px] text-gray-400">{u.url}</div>
            </div>
            <a
              href={u.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-[10px] font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
            >
              Open
            </a>
            <button
              onClick={() => copy(u.url)}
              data-testid={`obs-copy-${u.label.split(" ")[0].toLowerCase()}`}
              className="rounded-sm bg-[#1f63c9] px-3 py-1.5 font-oswald text-[10px] font-bold uppercase tracking-widest text-white hover:bg-[#2a74d8]"
            >
              {copied === u.url ? "Copied!" : "Copy"}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded border border-white/10 bg-black/40 p-3 text-xs text-gray-400">
        <div className="font-oswald text-[10px] font-bold uppercase tracking-widest text-[#ffd60a]">Quick OBS Steps</div>
        <ol className="mt-2 list-inside list-decimal space-y-1">
          <li>In OBS Studio, add Source → Browser</li>
          <li>Paste one of the URLs above</li>
          <li>Set Width <span className="font-mono text-white">1920</span> × Height <span className="font-mono text-white">1080</span></li>
          <li>Check "Shutdown source when not visible" = off (so weather keeps updating)</li>
          <li>Stream this scene 24/7 to your YouTube live channel</li>
        </ol>
      </div>
    </div>
  );
}

function BumperTab() {
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [durSec, setDurSec] = useState(6);
  const [saved, setSaved] = useState({ video_url: "", enabled: false });

  useEffect(() => {
    api.get("/bumper").then((r) => {
      setUrl(r.data.video_url || "");
      setEnabled(!!r.data.enabled);
      setDurSec(Math.round((r.data.duration_ms || 6000) / 1000));
      setSaved(r.data);
    }).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const payload = { video_url: url.trim(), enabled, duration_ms: Math.max(1, Math.min(60, Number(durSec) || 6)) * 1000 };
      const { data } = await api.put("/bumper", payload);
      setSaved(data);
      toast.success("Bumper updated");
    } catch {
      toast.error("Failed to save bumper");
    }
  };

  const clear = async () => {
    try {
      await api.put("/bumper", { video_url: "", enabled: false, duration_ms: 6000 });
      setUrl(""); setEnabled(false); setDurSec(6);
      setSaved({ video_url: "", enabled: false, duration_ms: 6000 });
      toast.success("Bumper cleared");
    } catch { toast.error("Failed to clear"); }
  };

  const isMp4 = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || "");
  const isYouTube = /youtu\.?be/i.test(url || "");

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            On‑Air Bumper Video
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Drop a short intro clip (MP4/WebM URL or YouTube link). It plays at the top of every broadcast loop as the signature GWN opener.
          Storm Mode auto‑skips the bumper so viewers see severe alerts immediately.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-6">
          <div className="md:col-span-4">
            <Label>Video URL (MP4/WebM or YouTube)</Label>
            <Input
              data-testid="admin-bumper-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…/gwn-bumper.mp4  or  https://youtu.be/…"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Duration (seconds)</Label>
            <Input
              data-testid="admin-bumper-duration"
              type="number"
              min={1}
              max={60}
              value={durSec}
              onChange={(e) => setDurSec(e.target.value)}
            />
          </div>
        </div>

        <label className="mt-4 inline-flex cursor-pointer items-center gap-2">
          <input
            data-testid="admin-bumper-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 accent-[#1f63c9]"
          />
          <span className="font-oswald text-xs uppercase tracking-widest text-white">
            Enable bumper at the start of every loop
          </span>
        </label>

        <div className="mt-4 flex gap-2">
          <Btn data-testid="admin-bumper-save" onClick={save}>Save Bumper</Btn>
          <button
            data-testid="admin-bumper-clear"
            onClick={clear}
            className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
          >
            Clear
          </button>
        </div>
      </div>

      {url && (
        <div className="glass rounded-sm p-6">
          <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">Preview</div>
          <div className="mt-3 aspect-video overflow-hidden rounded border border-white/10 bg-black">
            {isMp4 ? (
              <video src={url} autoPlay muted playsInline loop className="h-full w-full object-cover" />
            ) : isYouTube ? (
              <iframe
                title="Bumper Preview"
                src={`${url.replace("watch?v=", "embed/")}?autoplay=1&mute=1&controls=0`}
                className="h-full w-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                frameBorder="0"
              />
            ) : (
              <video src={url} autoPlay muted playsInline loop className="h-full w-full object-cover" />
            )}
          </div>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-gray-400">
            Saved: {saved.enabled ? "enabled" : "disabled"} · {saved.video_url ? "url set" : "no url"}
          </div>
        </div>
      )}
    </div>
  );
}

function CopyableUrl({ label, url, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  return (
    <div className="rounded-sm border border-white/15 bg-black/50 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="font-oswald text-[11px] uppercase tracking-widest text-[#ffd60a]">{label}</div>
        <div className="flex gap-1">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-8 items-center gap-1 rounded-sm border border-white/15 px-2 font-oswald text-[10px] uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
            aria-label="Open"
          >
            <ExternalLink className="h-3 w-3" /> Open
          </a>
          <button
            data-testid={testId}
            onClick={copy}
            className="inline-flex h-8 items-center gap-1 rounded-sm bg-[#1f63c9] px-3 font-oswald text-[10px] font-bold uppercase tracking-widest text-white hover:bg-[#2a74d8]"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
      <code className="block break-all font-mono text-xs text-gray-200">{url}</code>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="grid grid-cols-[36px_1fr] gap-4">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1f63c9] font-oswald text-sm font-bold text-white">
        {n}
      </div>
      <div>
        <div className="font-oswald text-base font-bold uppercase tracking-widest text-white">{title}</div>
        <div className="mt-2 space-y-2 text-sm text-gray-300">{children}</div>
      </div>
    </div>
  );
}

function StreamingSetupTab() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const broadcastUrl = `${origin}/broadcast?chrome=off`;
  const stormUrl = `${origin}/broadcast?chrome=off&layer=storm`;
  const rtmpUrl = "rtmp://a.rtmp.youtube.com/live2";

  return (
    <div className="space-y-6" data-testid="streaming-setup-tab">
      <CloudEncoderCard />
      <BroadcastFxCard />
      <PromoCard />
      <MusicCard />
      <VoiceCard />
      <WatchCard />
      <StreamHealthWidget />
      <details className="group glass rounded-sm border border-white/10" data-testid="streaming-other-ways">
        <summary className="cursor-pointer px-6 py-4 font-oswald text-sm font-bold uppercase tracking-widest text-gray-300 hover:text-white">
          Other ways to stream (OBS on your own computer, managed relays, background music) — optional
        </summary>
        <div className="space-y-6 border-t border-white/10 p-6">
      <CloudRelayGuide captureUrl={broadcastUrl} />
      {/* Hero */}
      <div className="glass rounded-sm border border-white/10 p-6">
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Option 1 · OBS on your own computer / VPS
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Everything you need to point OBS at your broadcast graphics and stream them to YouTube around the clock.
          Copy the capture URL below and follow the six steps.
        </p>
        <div className="mt-4 space-y-3">
          <CopyableUrl
            label="OBS Capture URL · Chrome-Free"
            url={broadcastUrl}
            testId="streaming-copy-broadcast"
          />
          <CopyableUrl
            label="Storm Mode Preview (adds strobe frame if a warning is active)"
            url={stormUrl}
            testId="streaming-copy-storm"
          />
          <CopyableUrl
            label="YouTube RTMP Ingest Server"
            url={rtmpUrl}
            testId="streaming-copy-rtmp"
          />
        </div>
      </div>

      {/* Steps */}
      <div className="glass rounded-sm p-6">
        <div className="mb-6 font-oswald text-sm uppercase tracking-widest text-gray-400">Six-Step Playbook</div>
        <div className="space-y-8">
          <Step n={1} title="Enable YouTube Live">
            <p>Go to <a className="text-[#ffd60a] underline" href="https://studio.youtube.com" target="_blank" rel="noreferrer">studio.youtube.com</a> → <span className="font-mono">Create → Go live</span>.</p>
            <p className="text-xs text-gray-400">First-time only: verify your channel by phone; there's a ~24-hour hold before live streaming unlocks.</p>
          </Step>

          <Step n={2} title="Create a Persistent Stream">
            <p>In YouTube Studio pick <span className="font-mono">Stream</span> (not Webcam). Title it something like <em>"Georgia Weather Network · 24/7 Live"</em>, category <span className="font-mono">News & Politics</span>, thumbnail = your GWN logo.</p>
            <p>Recommended settings:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Latency: <span className="font-mono text-white">Normal</span> (best for round-the-clock loops)</li>
              <li>DVR: <span className="font-mono text-white">On</span> (viewers can rewind)</li>
              <li>Made for kids: <span className="font-mono text-white">No</span></li>
              <li>Enable <span className="font-mono text-white">Auto-start</span> and <span className="font-mono text-white">Auto-stop</span></li>
            </ul>
            <p>Copy the <strong className="text-white">Stream key</strong> from the Stream settings panel — you'll paste it into OBS next.</p>
          </Step>

          <Step n={3} title="Install & Configure OBS Studio">
            <p>Download from <a className="text-[#ffd60a] underline" href="https://obsproject.com" target="_blank" rel="noreferrer">obsproject.com</a> (free, Win / Mac / Linux).</p>
            <p>Under <span className="font-mono">Settings</span>:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li><span className="font-mono text-white">Stream</span> → Service: <span className="font-mono">YouTube - RTMPS</span>, paste your stream key</li>
              <li><span className="font-mono text-white">Output</span> (Advanced): Encoder <span className="font-mono">NVENC H.264</span> / <span className="font-mono">Apple VT</span>, Bitrate <span className="font-mono">6000 Kbps</span> for 1080p60 (4500 for 30fps), Keyframe interval <span className="font-mono">2</span></li>
              <li><span className="font-mono text-white">Video</span>: Base + Output <span className="font-mono">1920×1080</span>, FPS <span className="font-mono">30</span> (60 if your GPU can handle it)</li>
              <li><span className="font-mono text-white">Audio</span>: <span className="font-mono">48 kHz Stereo</span></li>
            </ul>
          </Step>

          <Step n={4} title="Capture the Broadcast Graphics">
            <p>In OBS main window:</p>
            <ol className="ml-4 list-decimal space-y-1">
              <li><span className="font-mono text-white">Scenes</span> → <span className="font-mono">+</span> → name it "GWN Loop"</li>
              <li><span className="font-mono text-white">Sources</span> → <span className="font-mono">+</span> → <span className="font-mono">Browser</span></li>
              <li>URL = the OBS Capture URL above (already copied to your clipboard)</li>
              <li>Width <span className="font-mono text-white">1920</span> × Height <span className="font-mono text-white">1080</span></li>
              <li>Leave <em>Shutdown source when not visible</em> and <em>Refresh when scene becomes active</em> <span className="font-mono text-white">OFF</span> so weather keeps updating in the background.</li>
            </ol>
            <p className="text-xs text-gray-400">Optional overlays: add a bug logo (Image source), a tickering headline (Text with scroll filter), or your face for cut-ins (Video Capture Device).</p>
          </Step>

          <Step n={5} title="Test & Go Live">
            <p>Click <span className="font-mono text-white">Start Recording</span> in OBS for 60 seconds and play it back to confirm slides advance and the ticker scrolls.</p>
            <p>When ready: OBS <span className="font-mono text-white">Start Streaming</span> → YouTube Studio dashboard should show a green <em>Excellent connection</em> indicator → hit <span className="font-mono text-white">Go Live</span>.</p>
            <p className="text-xs text-gray-400">To keep it running 24/7: either leave your PC on with sleep/updates disabled, or run OBS on a small cloud GPU VM (Vultr / Contabo / Hetzner GPU) — ~$20–40/mo.</p>
          </Step>

          <Step n={6} title="Point the Homepage Back at Your Stream">
            <p>Once YouTube is live, copy the video URL (or your channel handle like <span className="font-mono">@georgiaweathernetwork</span>).</p>
            <p>Open <a className="text-[#ffd60a] underline" href="/admin?tab=stream" target="_self">Admin → 24/7 Stream</a> → paste it into <em>Auto-Detect Live Stream</em> → hit <span className="font-mono text-white">Find live</span>. The homepage player now embeds your own broadcast — closing the loop.</p>
          </Step>
        </div>
      </div>

      {/* Background music how-to */}
      <div className="glass rounded-sm border border-[#ffd60a]/30 p-6" data-testid="streaming-music-howto">
        <div className="flex items-center gap-2">
          <Music className="h-5 w-5 text-[#ffd60a]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Background Music (OBS Media Source)
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Layer instrumental country / americana under the graphics so GWN sounds like a broadcast, not a slideshow.
          Music runs in OBS as a separate audio source — it does <strong>not</strong> come from this app.
        </p>

        <div className="mt-4 rounded-sm border border-[#1f63c9]/40 bg-[#1f63c9]/10 p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#ffd60a]" />
            <div className="font-oswald text-xs font-bold uppercase tracking-widest text-white">
              Licensing First — YouTube Will Strike You
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-300">
            <strong>Never</strong> use tracks from Spotify, Apple Music, or ripped from other YouTube videos.
            Even instrumental country gets flagged. Use only <em>licensed-for-live-stream</em> sources listed below.
          </p>
        </div>

        <div className="mt-4">
          <div className="mb-2 font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Safe Music Sources</div>
          <div className="grid gap-2 md:grid-cols-2">
            {[
              {name: "YouTube Audio Library", url: "https://studio.youtube.com/", note: "Free · filter Genre = Country + Type = Instrumental. Cleared for any YouTube stream."},
              {name: "Epidemic Sound", url: "https://www.epidemicsound.com/music/genres/country/", note: "~$15/mo · big Americana catalog · cleared for live streams and monetized channels."},
              {name: "Artlist", url: "https://artlist.io/royalty-free-music/genre/country", note: "~$16/mo · high-quality instrumentals · lifetime license on downloaded tracks."},
              {name: "Uppbeat", url: "https://uppbeat.io/browse/genre/country", note: "Free tier (attribution) or ~$7/mo · country + folk instrumentals."},
              {name: "Pixabay Music", url: "https://pixabay.com/music/search/genre/country/", note: "100% free · no attribution required · quality varies."},
              {name: "Mubert Streams", url: "https://mubert.com/render", note: "AI-generated infinite music streams · cleared for live use · $14/mo."},
            ].map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="group rounded-sm border border-white/10 bg-black/50 p-3 hover:border-[#ffd60a]/60"
              >
                <div className="flex items-center justify-between">
                  <div className="font-oswald text-sm font-bold uppercase tracking-wider text-white group-hover:text-[#ffd60a]">{s.name}</div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-500 group-hover:text-[#ffd60a]" />
                </div>
                <div className="mt-1 text-xs text-gray-400">{s.note}</div>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-6">
          <Step n={1} title="Build a Track Folder">
            <p>Download ~2–4 hours of instrumental country/americana as MP3 or WAV to a folder like <span className="font-mono text-white">C:\GWN\music</span> (Windows) or <span className="font-mono text-white">~/GWN/music</span> (Mac). Aim for a mix of upbeat + mellow tempos so the mood matches the weather.</p>
          </Step>

          <Step n={2} title="Add a VLC Video Source or Media Source in OBS">
            <p>In OBS main window: <span className="font-mono text-white">Sources</span> → <span className="font-mono">+</span> → <span className="font-mono">VLC Video Source</span> (best) or <span className="font-mono">Media Source</span>.</p>
            <ul className="ml-4 list-disc space-y-1">
              <li><strong>VLC Video Source</strong>: click <span className="font-mono">+</span> under Playlist → <span className="font-mono">Add Folder</span> → point at your music folder. Enable <span className="font-mono text-white">Loop Playlist</span> and <span className="font-mono text-white">Shuffle Playlist</span>.</li>
              <li><strong>Media Source</strong> (fallback if VLC plugin not installed): add one file, enable <span className="font-mono text-white">Loop</span> — but this plays a single track forever. Prefer VLC.</li>
            </ul>
            <p className="text-xs text-gray-400">VLC ships built into OBS 30+. If missing on Mac, install VLC.app first then reopen OBS.</p>
          </Step>

          <Step n={3} title="Rename & Position the Audio Bus">
            <p>Right-click the new source → <span className="font-mono text-white">Rename</span> → "Background Bed". You can hide the video area by unchecking the eyeball icon — audio still plays.</p>
            <p>Open <span className="font-mono text-white">Audio Mixer</span> (bottom panel). Click the gear icon on "Background Bed" → <span className="font-mono">Advanced Audio Properties</span> → set <em>Audio Monitoring</em> = <span className="font-mono text-white">Monitor and Output</span> so you hear it while streaming.</p>
          </Step>

          <Step n={4} title="Duck the Music Under Alerts">
            <p>In Audio Mixer, right-click "Background Bed" → <span className="font-mono text-white">Filters</span> → <span className="font-mono">+</span> → <span className="font-mono">Compressor</span>.</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Ratio: <span className="font-mono text-white">6:1</span></li>
              <li>Threshold: <span className="font-mono text-white">-32 dB</span></li>
              <li>Sidechain/Ducking Source: point it at your microphone or the browser source</li>
              <li>Attack / Release: <span className="font-mono text-white">6 / 60 ms</span></li>
            </ul>
            <p>Result: whenever you cut in with the mic (or the alert graphic's audio fires), the bed automatically drops ~12 dB and pops back after you stop. Sounds broadcast-professional.</p>
          </Step>

          <Step n={5} title="Set the Bed Level & Test">
            <p>Set the "Background Bed" fader to around <span className="font-mono text-white">-22 dB to -18 dB</span> — quiet enough that a voiceover sits on top comfortably.</p>
            <p>Record 60 seconds, play it back. If the bed feels loud, drop 3 dB. If you can barely hear it, come up 3 dB. Iterate.</p>
            <p className="text-xs text-gray-400">Optional: add a second "Bumper Sting" media source that fires only on-demand via an OBS Studio hotkey when you cut to breaking coverage.</p>
          </Step>

          <Step n={6} title="Rotate the Playlist Weekly">
            <p>Return to your music source once a week, drag in 30–60 min of new tracks, remove the ones you've heard too many times. Regular listeners will notice repeats within a couple hours if the folder is under 2 hours long.</p>
          </Step>
        </div>
      </div>

      {/* Cheat sheet */}
      <div className="glass rounded-sm p-6">
        <div className="mb-4 font-oswald text-sm uppercase tracking-widest text-gray-400">Sanity Checks Before Going Live</div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-sm border border-white/10 bg-black/50 p-4">
            <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Bumper first</div>
            <p className="mt-1 text-sm text-gray-300">Upload your GWN sting under <a className="underline" href="/admin?tab=bumper">Admin → On-Air Bumper</a> so every loop opens with your signature intro.</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-black/50 p-4">
            <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Storm Mode test</div>
            <p className="mt-1 text-sm text-gray-300">Post a Custom Alert titled <span className="font-mono">Tornado Warning</span> — the red frame + polygon should appear on-air within a minute, and the bumper auto-skips.</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-black/50 p-4">
            <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Screen tearing?</div>
            <p className="mt-1 text-sm text-gray-300">OBS → Advanced → Renderer: <span className="font-mono">Direct3D 11</span> on Windows, <span className="font-mono">Metal</span> on Mac.</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-black/50 p-4">
            <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Music bed</div>
            <p className="mt-1 text-sm text-gray-300">Layer instrumental country under the graphics via an OBS VLC Video Source — full playbook is in the Background Music section above.</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-black/50 p-4">
            <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Bandwidth</div>
            <p className="mt-1 text-sm text-gray-300">~10 Mbps sustained upload for 1080p30 @ 6000 kbps. Use wired ethernet if possible.</p>
          </div>
        </div>
      </div>
        </div>
      </details>
    </div>
  );
}

function VideosTab() {

  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [desc, setDesc] = useState("");

  const load = () => api.get("/videos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim() || !url.trim()) return toast.error("Title and URL required");
    try {
      await api.post("/videos", { title, youtube_url: url, description: desc });
      setTitle(""); setUrl(""); setDesc("");
      await load();
      toast.success("Video added");
    } catch { toast.error("Failed to add video"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this video?")) return;
    try { await api.delete(`/videos/${id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Add Forecast Video</div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Title</Label>
            <Input data-testid={ADMIN.videoTitle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Wednesday Evening Forecast" />
          </div>
          <div>
            <Label>YouTube URL</Label>
            <Input data-testid={ADMIN.videoUrl} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." />
          </div>
          <div className="md:col-span-2">
            <Label>Description (optional)</Label>
            <Textarea data-testid={ADMIN.videoDesc} rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
        </div>
        <div className="mt-4">
          <Btn data-testid={ADMIN.videoAdd} onClick={add}><Plus className="h-4 w-4"/> Add Video</Btn>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 font-oswald text-lg font-bold uppercase tracking-widest text-white">
          Videos <span className="text-gray-400 text-sm normal-case tracking-normal font-plex">({items.length})</span>
        </div>
        <div className="space-y-2">
          {items.map((v) => (
            <div key={v.id} className="flex items-center gap-3 rounded border border-white/10 bg-black/40 p-3">
              <img src={v.thumbnail_url} alt="" className="h-16 w-28 rounded object-cover" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-oswald uppercase tracking-wider text-white">{v.title}</div>
                <div className="truncate text-xs text-gray-400">{v.youtube_url}</div>
              </div>
              <button
                data-testid={ADMIN.videoDelete(v.id)}
                onClick={() => del(v.id)}
                className="rounded-sm border border-white/15 p-2 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
                aria-label="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-gray-400">No videos yet.</div>}
        </div>
      </div>
    </div>
  );
}

function AlertsTab() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [msg, setMsg] = useState("");
  const [severity, setSeverity] = useState("warning");

  const load = () => api.get("/custom-alerts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim() || !msg.trim()) return toast.error("Title and message required");
    try {
      await api.post("/custom-alerts", { title, message: msg, severity, active: true });
      setTitle(""); setMsg("");
      await load();
      toast.success("Alert published");
    } catch { toast.error("Failed to publish"); }
  };
  const del = async (id) => {
    try { await api.delete(`/custom-alerts/${id}`); await load(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Publish Custom Alert</div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <Label>Title</Label>
            <Input data-testid={ADMIN.alertTitle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Severe Thunderstorm Watch" />
          </div>
          <div>
            <Label>Severity</Label>
            <select
              data-testid={ADMIN.alertSeverity}
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full rounded-sm border border-white/15 bg-black/60 px-3 py-2 text-sm text-white outline-none focus:border-[#1f63c9]"
            >
              <option value="severe">Severe</option>
              <option value="warning">Warning</option>
              <option value="watch">Watch</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <Label>Message</Label>
            <Textarea data-testid={ADMIN.alertMsg} rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Storm cells developing across north Georgia..." />
          </div>
        </div>
        <div className="mt-4">
          <Btn data-testid={ADMIN.alertAdd} onClick={add}><Plus className="h-4 w-4"/> Publish</Btn>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 font-oswald text-lg font-bold uppercase tracking-widest text-white">
          Active Alerts <span className="text-gray-400 text-sm normal-case tracking-normal font-plex">({items.length})</span>
        </div>
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="flex items-start gap-3 rounded border border-white/10 bg-black/40 p-3">
              <div className={`mt-1 h-3 w-3 rounded-full ${
                a.severity === "severe" ? "bg-[#d90429]" : a.severity === "warning" ? "bg-amber-500" : a.severity === "watch" ? "bg-yellow-400" : "bg-sky-400"
              }`} />
              <div className="min-w-0 flex-1">
                <div className="font-oswald uppercase tracking-wider text-white">{a.title}</div>
                <div className="text-xs text-gray-400">{a.message}</div>
              </div>
              <button
                data-testid={ADMIN.alertDelete(a.id)}
                onClick={() => del(a.id)}
                className="rounded-sm border border-white/15 p-2 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-gray-400">No custom alerts.</div>}
        </div>
      </div>
    </div>
  );
}

function TeamTab() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [bio, setBio] = useState("");
  const [img, setImg] = useState("");

  const load = () => api.get("/team").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name || !role || !img) return toast.error("Name, role and image are required");
    try {
      await api.post("/team", { name, role, bio, image_url: img, order: items.length + 1 });
      setName(""); setRole(""); setBio(""); setImg("");
      await load();
      toast.success("Team member added");
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/team/${id}`); await load(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Add Team Member</div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div><Label>Name</Label><Input data-testid={ADMIN.teamName} value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Role</Label><Input data-testid={ADMIN.teamRole} value={role} onChange={(e) => setRole(e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Image URL</Label><Input data-testid={ADMIN.teamImg} value={img} onChange={(e) => setImg(e.target.value)} placeholder="https://..." /></div>
          <div className="md:col-span-2"><Label>Bio</Label><Textarea data-testid={ADMIN.teamBio} rows={2} value={bio} onChange={(e) => setBio(e.target.value)} /></div>
        </div>
        <div className="mt-4"><Btn data-testid={ADMIN.teamAdd} onClick={add}><Plus className="h-4 w-4" /> Add</Btn></div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 font-oswald text-lg font-bold uppercase tracking-widest text-white">
          Roster <span className="text-gray-400 text-sm normal-case tracking-normal font-plex">({items.length})</span>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {items.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded border border-white/10 bg-black/40 p-3">
              <img src={m.image_url} alt="" className="h-16 w-16 rounded-full object-cover" />
              <div className="min-w-0 flex-1">
                <div className="font-oswald uppercase tracking-wider text-white">{m.name}</div>
                <div className="text-xs text-[#ffd60a]">{m.role}</div>
                <div className="line-clamp-2 text-xs text-gray-400">{m.bio}</div>
              </div>
              <button
                data-testid={ADMIN.teamDelete(m.id)}
                onClick={() => del(m.id)}
                className="rounded-sm border border-white/15 p-2 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CamerasTab() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [cropPct, setCropPct] = useState(0);
  const [handle, setHandle] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingPack, setLoadingPack] = useState(false);

  const load = () => api.get("/cameras/admin").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const loadStarterPack = async () => {
    setLoadingPack(true);
    try {
      const { data } = await api.post("/cameras/starter-pack");
      await load();
      const notLive = (data.skipped || []).filter((s) => (s.reason || "").includes("not broadcasting"));
      if (data.count > 0) {
        toast.success(
          `Added ${data.count} Georgia live cam${data.count === 1 ? "" : "s"}` +
          (notLive.length ? ` · skipped ${notLive.length} not live right now` : "")
        );
      } else if (notLive.length && notLive.length === (data.skipped || []).length) {
        toast.warning(
          `None of the ${notLive.length} channels are broadcasting live right now — try again later`
        );
      } else if (data.skipped?.length) {
        toast.info("Starter pack already loaded — all cameras are in your roster");
      } else {
        toast.warning("No live streams found right now — try again later");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Starter pack failed";
      toast.error(typeof msg === "string" ? msg : "Starter pack failed");
    } finally {
      setLoadingPack(false);
    }
  };

  const add = async () => {
    if (!name.trim() || !url.trim()) return toast.error("Name and YouTube URL required");
    setSaving(true);
    try {
      const cropVal = Math.max(0, Math.min(Number(cropPct) || 0, 30));
      await api.post("/cameras", { name, location, youtube_url: url, enabled: true, crop_bottom_pct: cropVal, channel_handle: handle.trim() });
      setName(""); setLocation(""); setUrl(""); setCropPct(0); setHandle("");
      await load();
      toast.success("Camera added");
    } catch (e) {
      const msg = e.response?.data?.detail || "Add failed";
      toast.error(typeof msg === "string" ? msg : "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (cam) => {
    try { await api.put(`/cameras/${cam.id}`, { enabled: !cam.enabled }); await load(); }
    catch { toast.error("Toggle failed"); }
  };

  const setCrop = async (cam, val) => {
    const crop = Math.max(0, Math.min(Number(val) || 0, 30));
    try { await api.put(`/cameras/${cam.id}`, { crop_bottom_pct: crop }); await load(); toast.success(`Crop set to ${crop}%`); }
    catch { toast.error("Crop update failed"); }
  };

  const bench = async (cam, hours) => {
    try {
      await api.post(`/cameras/${cam.id}/bench`, { hours });
      await load();
      toast.success(hours > 0 ? `${cam.name} hidden from the wall for ${hours}h` : `${cam.name} is back in rotation`);
    } catch { toast.error("Bench update failed"); }
  };

  const move = async (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[i], b = items[target];
    try {
      await Promise.all([
        api.put(`/cameras/${a.id}`, { order: b.order }),
        api.put(`/cameras/${b.id}`, { order: a.order }),
      ]);
      await load();
    } catch { toast.error("Reorder failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this camera?")) return;
    try { await api.delete(`/cameras/${id}`); await load(); toast.success("Removed"); }
    catch { toast.error("Delete failed"); }
  };

  const enabledCount = items.filter((c) => c.enabled).length;

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="flex items-center gap-2">
          <Video className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Add Live Camera
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Paste any YouTube live-stream URL (skyline, beach, storm chaser, etc.). The Live Camera Wall shows the first 4 enabled cameras in a 2×2 grid inside the broadcast loop.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Name</Label>
            <Input data-testid="admin-cam-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tybee Island Pier" />
          </div>
          <div>
            <Label>Location (optional)</Label>
            <Input data-testid="admin-cam-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Coastal GA" />
            <div className="mt-2">
              <Label>Channel handle (optional · auto-heals dead streams)</Label>
              <Input data-testid="admin-cam-handle" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@FOX5Atlanta" />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>YouTube Live URL or Video ID</Label>
            <Input data-testid="admin-cam-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=... or 11-char ID" />
          </div>
          <div className="md:col-span-2">
            <Label>Crop Bottom % (hides tickers/chyrons, 0–30)</Label>
            <Input
              data-testid="admin-cam-crop"
              type="number"
              min={0}
              max={30}
              value={cropPct}
              onChange={(e) => setCropPct(e.target.value)}
              placeholder="e.g. 12 for a TV-station ticker"
            />
          </div>
        </div>
        <div className="mt-4">
          <Btn data-testid="admin-cam-add" onClick={add} disabled={saving}>
            <Plus className="h-4 w-4" /> {saving ? "Adding…" : "Add Camera"}
          </Btn>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-xl">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#ffd60a]" />
              <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
                Georgia Cam Starter Pack
              </div>
            </div>
            <p className="mt-1 text-sm text-gray-400">
              One-click seed of 5 curated Georgia YouTube channels — FOX 5 Atlanta, WSB-TV, 11Alive, Atlanta News First, and the Georgia Aquarium. We resolve each channel's currently-live video at load time, skip any duplicates in your roster, and drop them into the Camera Wall for you.
            </p>
          </div>
          <Btn
            data-testid="admin-cam-starter-pack"
            onClick={loadStarterPack}
            disabled={loadingPack}
            className="whitespace-nowrap"
          >
            <Zap className="h-4 w-4" /> {loadingPack ? "Loading…" : "Load Starter Pack"}
          </Btn>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Camera Roster
          </div>
          <div className="rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-xs text-white">
            {enabledCount} on · showing top {Math.min(enabledCount, 4)} in wall
          </div>
        </div>

        {items.length === 0 ? (
          <div className="rounded border border-dashed border-white/15 p-8 text-center">
            <Video className="mx-auto h-10 w-10 text-gray-500" />
            <div className="mt-3 font-oswald text-sm uppercase tracking-widest text-white">
              No cameras yet
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Add YouTube live-stream URLs above. The Camera Wall slide will appear in the broadcast loop once at least one camera is enabled.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((cam, i) => (
              <div
                key={cam.id}
                data-testid={`admin-cam-row-${cam.id}`}
                className={`flex items-center gap-3 rounded border p-3 ${cam.enabled ? "border-white/10 bg-black/40" : "border-white/5 bg-black/20 opacity-60"}`}
              >
                <img
                  src={`https://img.youtube.com/vi/${cam.youtube_id}/mqdefault.jpg`}
                  alt=""
                  className="h-14 w-24 rounded object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <div className="truncate font-oswald text-sm uppercase tracking-wider text-white">{cam.name}</div>
                      {cam.is_live === true && <span className="rounded-full bg-green-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-green-300" data-testid={`cam-live-${cam.id}`}>Live</span>}
                      {cam.is_live === false && <span className="rounded-full bg-red-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-red-300" title="Stream is offline — hidden from the broadcast wall until it comes back" data-testid={`cam-offline-${cam.id}`}>Offline · hidden</span>}
                      {cam.benched_until && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-300" title="Manually hidden from the wall" data-testid={`cam-benched-${cam.id}`}>Hidden until {new Date(cam.benched_until).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
                      {cam.channel_handle && <span className="font-mono text-[9px] text-gray-500">{cam.channel_handle}</span>}
                    </div>
                    {i < 4 && cam.enabled && (
                      <span className="rounded-full bg-[#1f63c9] px-1.5 py-0.5 font-oswald text-[9px] font-bold uppercase tracking-widest text-white">
                        On Wall · Tile {i + 1}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-gray-400">{cam.location || "—"}</div>
                  <div className="truncate font-mono text-[10px] text-gray-500">ID: {cam.youtube_id}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="font-oswald text-[9px] uppercase tracking-widest text-gray-400" title="Feed showing an offline / 'be right back' slate? Pull it off the wall for a while.">Hide feed</div>
                  <div className="flex items-center gap-1">
                    {cam.benched_until ? (
                      <button onClick={() => bench(cam, 0)} data-testid={`admin-cam-unbench-${cam.id}`} className="rounded-sm border border-amber-400/40 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-amber-300 hover:border-amber-300">Unhide</button>
                    ) : (
                      <>
                        <button onClick={() => bench(cam, 2)} data-testid={`admin-cam-bench-2h-${cam.id}`} className="rounded-sm border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]">2h</button>
                        <button onClick={() => bench(cam, 12)} data-testid={`admin-cam-bench-12h-${cam.id}`} className="rounded-sm border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]">12h</button>
                        <button onClick={() => bench(cam, 24)} data-testid={`admin-cam-bench-24h-${cam.id}`} className="rounded-sm border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]">24h</button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="font-oswald text-[9px] uppercase tracking-widest text-gray-400">Crop%</div>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    defaultValue={cam.crop_bottom_pct || 0}
                    onBlur={(e) => {
                      const v = Math.max(0, Math.min(Number(e.target.value) || 0, 30));
                      if (v !== (cam.crop_bottom_pct || 0)) setCrop(cam, v);
                    }}
                    data-testid={`admin-cam-crop-${cam.id}`}
                    className="w-14 rounded-sm border border-white/15 bg-black/60 px-1.5 py-1 text-center text-xs text-white outline-none focus:border-[#1f63c9]"
                    aria-label="Crop bottom percent"
                  />
                </div>
                <button
                  onClick={() => toggle(cam)}
                  data-testid={`admin-cam-toggle-${cam.id}`}
                  role="switch"
                  aria-checked={cam.enabled}
                  className={`relative h-6 w-11 rounded-full transition-colors ${cam.enabled ? "bg-[#1f63c9]" : "bg-white/10"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${cam.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    data-testid={`admin-cam-up-${cam.id}`}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                    data-testid={`admin-cam-down-${cam.id}`}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
                <button
                  onClick={() => del(cam.id)}
                  data-testid={`admin-cam-delete-${cam.id}`}
                  className="rounded-sm border border-white/15 p-2 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 rounded border border-white/10 bg-black/40 p-3 text-xs text-gray-400">
          <div className="font-oswald text-[10px] font-bold uppercase tracking-widest text-[#ffd60a]">Finding Georgia Live Cams</div>
          <p className="mt-1">
            Search YouTube for phrases like <span className="font-mono text-white">"Atlanta skyline live"</span>, <span className="font-mono text-white">"Tybee Island live cam"</span>, <span className="font-mono text-white">"Jekyll Island webcam"</span>, or your local TV station's tower cam. Copy the video URL and paste above.
          </p>
        </div>
      </div>
    </div>
  );
}


function SlidesTab() {
  const [slides, setSlides] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => api.get("/broadcast/config").then((r) => { setSlides(r.data.slides || []); setDirty(false); });
  useEffect(() => { load(); }, []);

  const move = (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= slides.length) return;
    const next = [...slides];
    [next[i], next[target]] = [next[target], next[i]];
    setSlides(next); setDirty(true);
  };

  const toggle = (i) => {
    const next = slides.map((s, j) => (j === i ? { ...s, enabled: !s.enabled } : s));
    setSlides(next); setDirty(true);
  };

  const setDuration = (i, seconds) => {
    const s = Math.max(3, Math.min(60, Number(seconds) || 8));
    const next = slides.map((sl, j) => (j === i ? { ...sl, duration_ms: s * 1000 } : sl));
    setSlides(next); setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { slides: slides.map((s) => ({ id: s.id, enabled: s.enabled, duration_ms: s.duration_ms })) };
      const { data } = await api.put("/broadcast/config", payload);
      setSlides(data.slides || []); setDirty(false);
      toast.success("Broadcast loop saved");
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!confirm("Restore the default slide order and durations?")) return;
    try {
      const { data } = await api.post("/broadcast/config/reset");
      setSlides(data.slides || []); setDirty(false);
      toast.success("Defaults restored");
    } catch { toast.error("Reset failed"); }
  };

  const totalSec = slides.filter((s) => s.enabled).reduce((sum, s) => sum + (s.duration_ms || 0), 0) / 1000;
  const enabledCount = slides.filter((s) => s.enabled).length;

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Sliders className="h-5 w-5 text-[#1f63c9]" />
              <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
                Broadcast Loop Editor
              </div>
            </div>
            <p className="mt-1 text-sm text-gray-400">
              Toggle graphics on or off, reorder them with the arrows, and tune each slide's duration. Applies to the on-page loop and the OBS/`/broadcast` route within about a minute.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-xs text-white">
              {enabledCount} on · {Math.round(totalSec)}s per cycle
            </div>
            <button
              onClick={resetDefaults}
              data-testid="admin-slides-reset"
              className="flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Defaults
            </button>
            <Btn
              data-testid="admin-slides-save"
              onClick={save}
              disabled={saving || !dirty}
              className={dirty ? "" : "opacity-60"}
            >
              {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
            </Btn>
          </div>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-3 grid grid-cols-[36px_1fr_120px_60px_60px] items-center gap-3 border-b border-white/10 pb-2 font-oswald text-[10px] uppercase tracking-widest text-gray-400 sm:grid-cols-[36px_1fr_140px_120px_120px]">
          <div>#</div>
          <div>Graphic</div>
          <div>Duration</div>
          <div className="text-center">Enabled</div>
          <div className="text-center">Order</div>
        </div>
        <div className="space-y-2">
          {slides.map((s, i) => {
            const seconds = Math.round((s.duration_ms || 8000) / 1000);
            return (
              <div
                key={s.id}
                data-testid={`admin-slide-row-${s.id}`}
                className={`grid grid-cols-[36px_1fr_120px_60px_60px] items-center gap-3 rounded border p-3 sm:grid-cols-[36px_1fr_140px_120px_120px] ${
                  s.enabled ? "border-white/10 bg-black/40" : "border-white/5 bg-black/20 opacity-60"
                }`}
              >
                <div className="font-mono text-xs text-gray-400">{String(i + 1).padStart(2, "0")}</div>
                <div>
                  <div className="font-oswald text-sm uppercase tracking-wider text-white">{s.label || s.id}</div>
                  <div className="font-mono text-[10px] text-gray-500">{s.id}</div>
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={3}
                    max={60}
                    value={seconds}
                    onChange={(e) => setDuration(i, e.target.value)}
                    data-testid={`admin-slide-duration-${s.id}`}
                    className="w-16 rounded-sm border border-white/15 bg-black/60 px-2 py-1 text-center text-sm text-white outline-none focus:border-[#1f63c9]"
                  />
                  <span className="font-mono text-xs text-gray-400">sec</span>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={() => toggle(i)}
                    role="switch"
                    aria-checked={s.enabled}
                    data-testid={`admin-slide-toggle-${s.id}`}
                    className={`relative h-6 w-11 rounded-full transition-colors ${
                      s.enabled ? "bg-[#1f63c9]" : "bg-white/10"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                        s.enabled ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    data-testid={`admin-slide-up-${s.id}`}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === slides.length - 1}
                    data-testid={`admin-slide-down-${s.id}`}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
          {slides.length === 0 && (
            <div className="rounded border border-dashed border-white/15 p-6 text-center text-sm text-gray-400">
              Loading slides…
            </div>
          )}
        </div>
        <div className="mt-4 text-xs text-gray-500">
          Tip · The <span className="font-mono text-gray-300">alert</span> slide only appears when there's an active NWS
          alert for Georgia — disable it here if you'd like to suppress in-loop alert takeovers.
        </div>
      </div>
    </div>
  );
}


function ArticlesTab() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState("Trey Tonnessen");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [publish, setPublish] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/articles/admin").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditingId(null);
    setTitle(""); setSubtitle(""); setBody(""); setAuthor("Trey Tonnessen"); setTags([]); setTagInput(""); setPublish(true);
  };

  const toggleTag = (t) => {
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const addCustomTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput("");
  };

  const save = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body are required");
    setSaving(true);
    try {
      const payload = { title, subtitle, body, author, tags, is_published: publish };
      if (editingId) {
        await api.put(`/articles/${editingId}`, payload);
        toast.success("Article updated");
      } else {
        await api.post("/articles", payload);
        toast.success("Article published");
      }
      reset();
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const edit = (a) => {
    setEditingId(a.id);
    setTitle(a.title); setSubtitle(a.subtitle || ""); setBody(a.body);
    setAuthor(a.author || "Trey Tonnessen");
    setTags(a.tags || []);
    setPublish(!!a.is_published);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const togglePublish = async (a) => {
    try { await api.put(`/articles/${a.id}`, { is_published: !a.is_published }); await load(); }
    catch { toast.error("Toggle failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this article?")) return;
    try { await api.delete(`/articles/${id}`); if (editingId === id) reset(); await load(); toast.success("Removed"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            {editingId ? "Edit Article" : "Publish New Forecast Article"}
          </div>
          {editingId && (
            <button
              onClick={reset}
              className="ml-auto rounded-sm border border-white/15 px-3 py-1 font-oswald text-[10px] uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
            >
              Cancel · New Article
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Publish a meteorologist's discussion. Only the most recent published article appears on the home page.
          Blank lines separate paragraphs. Prefix a line with <span className="font-mono text-white">##</span> to render it as a subheading.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Title</Label>
            <Input data-testid="admin-article-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Heat Holds Through Midweek Before Storms Return" />
          </div>
          <div>
            <Label>Author</Label>
            <Input data-testid="admin-article-author" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Trey Tonnessen" />
          </div>
          <div className="md:col-span-2">
            <Label>Subtitle (optional deck / summary line)</Label>
            <Input data-testid="admin-article-subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Coastal dewpoints climb, HRRR hints at a Wednesday shortwave" />
          </div>
          <div className="md:col-span-2">
            <Label>Body</Label>
            <Textarea
              data-testid="admin-article-body"
              rows={16}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="A heat-dome ridge is anchored over the Southeast today...\n\n## Model Trends\n\nHRRR is trending drier through Monday..."
              className="font-mono text-sm"
            />
            <div className="mt-1 text-[11px] text-gray-500">{body.length.toLocaleString()} chars · {body.split(/\n\s*\n/).filter(Boolean).length} paragraphs</div>
          </div>
          <div className="md:col-span-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2" data-testid="admin-article-tag-presets">
              {TAG_PRESETS.map((p) => (
                <TagChip
                  key={p.id}
                  tag={p.id}
                  active={tags.includes(p.id)}
                  onClick={() => toggleTag(p.id)}
                />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Input
                data-testid="admin-article-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomTag(); } }}
                placeholder="Add a custom tag and press Enter"
                className="max-w-xs"
              />
              <button
                type="button"
                onClick={addCustomTag}
                data-testid="admin-article-tag-add"
                className="inline-flex h-9 items-center gap-1 rounded-sm border border-white/15 px-3 font-oswald text-[10px] uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
              >
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            {tags.length > 0 && (
              <div className="mt-3">
                <div className="mb-1 font-oswald text-[10px] uppercase tracking-widest text-gray-400">Selected</div>
                <div className="flex flex-wrap gap-2" data-testid="admin-article-tag-selected">
                  {tags.map((t) => (
                    <TagChip
                      key={t}
                      tag={t}
                      active
                      removable
                      onRemove={() => setTags((prev) => prev.filter((x) => x !== t))}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <label className="mt-4 inline-flex cursor-pointer items-center gap-2">
          <input
            data-testid="admin-article-publish"
            type="checkbox"
            checked={publish}
            onChange={(e) => setPublish(e.target.checked)}
            className="h-4 w-4 accent-[#1f63c9]"
          />
          <span className="font-oswald text-xs uppercase tracking-widest text-white">
            Publish immediately
          </span>
        </label>

        <div className="mt-4">
          <Btn data-testid="admin-article-save" onClick={save} disabled={saving}>
            <Plus className="h-4 w-4" /> {saving ? "Saving…" : (editingId ? "Save Changes" : "Publish Article")}
          </Btn>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 font-oswald text-lg font-bold uppercase tracking-widest text-white">
          Article History ({items.length})
        </div>
        {items.length === 0 ? (
          <div className="rounded border border-dashed border-white/15 p-8 text-center">
            <Newspaper className="mx-auto h-10 w-10 text-gray-500" />
            <div className="mt-3 font-oswald text-sm uppercase tracking-widest text-white">No articles yet</div>
            <p className="mt-1 text-xs text-gray-400">Publish your first meteorologist's discussion above.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-article-row-${a.id}`}
                className={`flex items-center gap-3 rounded border p-3 ${a.is_published ? "border-white/10 bg-black/40" : "border-white/5 bg-black/20 opacity-60"}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-oswald text-sm uppercase tracking-wider text-white">{a.title}</div>
                  <div className="truncate text-xs text-gray-400">{a.subtitle || "—"}</div>
                  <div className="truncate font-mono text-[10px] text-gray-500">
                    {a.author || "?"} · {a.published_at ? new Date(a.published_at).toLocaleString() : "unpublished"}
                  </div>
                  {(a.tags || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {a.tags.map((t) => (
                        <TagChip key={t} tag={t} size="xs" />
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => togglePublish(a)}
                  data-testid={`admin-article-toggle-${a.id}`}
                  role="switch"
                  aria-checked={a.is_published}
                  className={`relative h-6 w-11 rounded-full transition-colors ${a.is_published ? "bg-[#1f63c9]" : "bg-white/10"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${a.is_published ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <button
                  onClick={() => edit(a)}
                  data-testid={`admin-article-edit-${a.id}`}
                  className="rounded-sm border border-white/15 px-2 py-1 font-oswald text-[10px] uppercase tracking-widest text-white hover:border-[#ffd60a] hover:text-[#ffd60a]"
                >
                  Edit
                </button>
                <button
                  onClick={() => del(a.id)}
                  data-testid={`admin-article-delete-${a.id}`}
                  className="rounded-sm border border-white/15 p-2 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function SponsorsTab() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [logo, setLogo] = useState("");
  const [click, setClick] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/sponsors/admin").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim() || !logo.trim()) return toast.error("Name and logo URL are required");
    setSaving(true);
    try {
      await api.post("/sponsors", { name, tagline, logo_url: logo, click_url: click, enabled: true });
      setName(""); setTagline(""); setLogo(""); setClick("");
      await load();
      toast.success("Sponsor added");
    } catch (e) {
      const msg = e.response?.data?.detail || "Add failed";
      toast.error(typeof msg === "string" ? msg : "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s) => {
    try { await api.put(`/sponsors/${s.id}`, { enabled: !s.enabled }); await load(); }
    catch { toast.error("Toggle failed"); }
  };

  const move = async (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[i], b = items[target];
    try {
      await Promise.all([
        api.put(`/sponsors/${a.id}`, { order: b.order }),
        api.put(`/sponsors/${b.id}`, { order: a.order }),
      ]);
      await load();
    } catch { toast.error("Reorder failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this sponsor?")) return;
    try { await api.delete(`/sponsors/${id}`); await load(); toast.success("Removed"); }
    catch { toast.error("Delete failed"); }
  };

  const enabledCount = items.filter((s) => s.enabled).length;

  return (
    <div className="space-y-6">
      <div className="glass flex flex-col gap-4 rounded-sm p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="media-kit-card">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#ffd60a]" />
            <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Sponsorship Media Kit</div>
          </div>
          <p className="mt-1 text-sm text-gray-400">A ready-to-send 6-page PDF pitch for prospects — programming, inventory, packages &amp; contact.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <a href="/gawx-sponsor-kit.pdf" download="GAWX-Sponsorship-Media-Kit.pdf" data-testid="download-media-kit"
             className="inline-flex items-center gap-2 rounded-md bg-[#1f63c9] px-5 py-2.5 font-oswald text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#2a74d8]">
            <Download className="h-4 w-4" /> Download PDF
          </a>
          <a href="/gawx-sponsor-kit.html" target="_blank" rel="noopener noreferrer" data-testid="preview-media-kit"
             className="inline-flex items-center gap-2 rounded-md bg-white/10 px-4 py-2.5 font-oswald text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-white/20">
            Preview
          </a>
        </div>
      </div>
      <div className="glass rounded-sm p-6">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Add Sponsor
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Each enabled sponsor rotates into the "Sponsored By" slot in the broadcast loop.
          Multiple sponsors cycle deterministically — consecutive appearances show different logos.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Sponsor Name</Label>
            <Input data-testid="admin-sponsor-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Peach State Roofing Co." />
          </div>
          <div>
            <Label>Click / Display URL (optional)</Label>
            <Input data-testid="admin-sponsor-click" value={click} onChange={(e) => setClick(e.target.value)} placeholder="https://peachstateroofing.com" />
          </div>
          <div className="md:col-span-2">
            <Label>Logo URL (PNG/JPG/SVG, transparent PNG works best)</Label>
            <Input data-testid="admin-sponsor-logo" value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://cdn.example.com/logo.png" />
          </div>
          <div className="md:col-span-2">
            <Label>Tagline (optional)</Label>
            <Input data-testid="admin-sponsor-tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder='"Trusted Georgia roofers since 1998"' />
          </div>
        </div>
        <div className="mt-4">
          <Btn data-testid="admin-sponsor-add" onClick={add} disabled={saving}>
            <Plus className="h-4 w-4" /> {saving ? "Adding…" : "Add Sponsor"}
          </Btn>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Sponsor Roster
          </div>
          <div className="rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-xs text-white">
            {enabledCount} enabled · rotates in "Sponsored By" slot
          </div>
        </div>

        {items.length === 0 ? (
          <div className="rounded border border-dashed border-white/15 p-8 text-center">
            <Award className="mx-auto h-10 w-10 text-gray-500" />
            <div className="mt-3 font-oswald text-sm uppercase tracking-widest text-white">
              No sponsors yet
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Add your first paying advertiser above. The Sponsored By slide only appears in the loop once at least one sponsor is enabled.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((s, i) => (
              <div
                key={s.id}
                data-testid={`admin-sponsor-row-${s.id}`}
                className={`flex items-center gap-3 rounded border p-3 ${s.enabled ? "border-white/10 bg-black/40" : "border-white/5 bg-black/20 opacity-60"}`}
              >
                <div className="flex h-16 w-28 items-center justify-center overflow-hidden rounded bg-black/70">
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <Award className="h-6 w-6 text-gray-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-oswald text-sm uppercase tracking-wider text-white">{s.name}</div>
                  <div className="truncate text-xs text-gray-400">{s.tagline || "—"}</div>
                  <div className="truncate font-mono text-[10px] text-gray-500">{s.click_url || "no click URL"}</div>
                </div>
                <button
                  onClick={() => toggle(s)}
                  data-testid={`admin-sponsor-toggle-${s.id}`}
                  role="switch"
                  aria-checked={s.enabled}
                  className={`relative h-6 w-11 rounded-full transition-colors ${s.enabled ? "bg-[#1f63c9]" : "bg-white/10"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${s.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    data-testid={`admin-sponsor-up-${s.id}`}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                    data-testid={`admin-sponsor-down-${s.id}`}
                    className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
                <button
                  onClick={() => del(s.id)}
                  data-testid={`admin-sponsor-delete-${s.id}`}
                  className="rounded-sm border border-white/15 p-2 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 rounded border border-white/10 bg-black/40 p-3 text-xs text-gray-400">
          <div className="font-oswald text-[10px] font-bold uppercase tracking-widest text-[#ffd60a]">Selling the Slot</div>
          <p className="mt-1">
            Suggested rate cards: a single sponsor gets ~1 slot per broadcast loop (~2–3 min). With 4 rotating sponsors, each averages every 4th appearance. Charge more for the exclusive first position — reorder via the arrows.
          </p>
        </div>
      </div>
    </div>
  );
}


function TickerTab() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("routine");
  const [expiresHours, setExpiresHours] = useState("24");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ticker/admin").then((r) => setItems(r.data)).catch(() => {});
  const renew = async (it, hours) => {
    try { await api.put(`/ticker/${it.id}`, { expires_hours: hours }); await load(); toast.success(hours > 0 ? `Headline renewed for ${hours}h` : "Headline set to never expire"); }
    catch { toast.error("Renew failed"); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!text.trim()) return toast.error("Ticker text is required");
    setSaving(true);
    try {
      await api.post("/ticker", { text: text.trim(), priority, enabled: true, expires_hours: Number(expiresHours) });
      setText(""); setPriority("routine");
      await load();
      toast.success("Ticker item added");
    } catch { toast.error("Add failed"); } finally { setSaving(false); }
  };
  const toggle = async (it) => {
    try { await api.put(`/ticker/${it.id}`, { enabled: !it.enabled }); await load(); }
    catch { toast.error("Toggle failed"); }
  };
  const swapPriority = async (it) => {
    const p = it.priority === "urgent" ? "routine" : "urgent";
    try { await api.put(`/ticker/${it.id}`, { priority: p }); await load(); toast.success(`Set to ${p}`); }
    catch { toast.error("Update failed"); }
  };
  const move = async (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[i], b = items[target];
    try {
      await Promise.all([
        api.put(`/ticker/${a.id}`, { order: b.order }),
        api.put(`/ticker/${b.id}`, { order: a.order }),
      ]);
      await load();
    } catch { toast.error("Reorder failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this ticker item?")) return;
    try { await api.delete(`/ticker/${id}`); await load(); toast.success("Removed"); }
    catch { toast.error("Delete failed"); }
  };

  const enabledCount = items.filter((i) => i.enabled).length;
  const urgentCount = items.filter((i) => i.enabled && i.priority === "urgent").length;

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            News Desk Ticker
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          The on-air ticker builds itself from live data — active NWS warnings and advisories, the SPC outlook, current
          conditions, the Atlanta forecast and upcoming gamedays — and mixes in the headlines you add here.
          Use <span className="font-mono text-white">URGENT</span> for breaking news (turns the bar red). Every headline
          expires automatically so nothing stale stays on air.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-8">
          <div className="md:col-span-4">
            <Label>Ticker text</Label>
            <Input
              data-testid="admin-ticker-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="ATL Public Schools closed Wednesday · Truist Park Braves game rescheduled for 4 PM"
              maxLength={200}
            />
            <div className="mt-1 text-[11px] text-gray-500">{text.length}/200 chars</div>
          </div>
          <div className="md:col-span-2">
            <Label>Priority</Label>
            <select
              data-testid="admin-ticker-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="h-10 w-full rounded-sm border border-white/10 bg-black/60 px-3 text-sm text-white outline-none focus:border-[#1f63c9]"
            >
              <option value="routine">Routine</option>
              <option value="urgent">Urgent · Breaking</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Runs for</Label>
            <select
              data-testid="admin-ticker-expires"
              value={expiresHours}
              onChange={(e) => setExpiresHours(e.target.value)}
              className="h-10 w-full rounded-sm border border-white/10 bg-black/60 px-3 text-sm text-white outline-none focus:border-[#1f63c9]"
            >
              <option value="6">6 hours</option>
              <option value="24">24 hours</option>
              <option value="72">3 days</option>
              <option value="168">1 week</option>
              <option value="0">Until removed</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <Btn data-testid="admin-ticker-add" onClick={add} disabled={saving}>
            <Plus className="h-4 w-4" /> {saving ? "Adding…" : "Add To Ticker"}
          </Btn>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
            Live Ticker Feed ({items.length})
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-xs text-white">
              {enabledCount} live
            </span>
            {urgentCount > 0 && (
              <span className="rounded-full bg-[#1f63c9] px-3 py-1 font-mono text-xs text-white">
                {urgentCount} urgent
              </span>
            )}
          </div>
        </div>
        {items.length === 0 ? (
          <div className="rounded border border-dashed border-white/15 p-8 text-center">
            <MessageSquare className="mx-auto h-10 w-10 text-gray-500" />
            <div className="mt-3 font-oswald text-sm uppercase tracking-widest text-white">Ticker is empty</div>
            <p className="mt-1 text-xs text-gray-400">Add your first headline above. The bottom ticker only renders when at least one item is live.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div
                key={it.id}
                data-testid={`admin-ticker-row-${it.id}`}
                className={`flex items-center gap-3 rounded border p-3 ${it.enabled ? "border-white/10 bg-black/40" : "border-white/5 bg-black/20 opacity-60"}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-sm px-2 py-0.5 font-oswald text-[9px] font-bold uppercase tracking-widest ${it.priority === "urgent" ? "bg-[#1f63c9] text-white" : "bg-[#ffd60a] text-black"}`}>
                      {it.priority}
                    </span>
                    <span className="truncate font-oswald text-sm uppercase tracking-wider text-white">{it.text}</span>
                    {it.expired && <span className="rounded-full bg-white/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-gray-300" data-testid={`admin-ticker-expired-${it.id}`}>Expired · off air</span>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 font-mono text-[10px] text-gray-500">
                    <span>{it.created_at ? new Date(it.created_at).toLocaleString() : "—"}</span>
                    <span className={it.expired ? "text-gray-500" : "text-emerald-300"}>{it.expires_at ? `${it.expired ? "expired" : "expires"} ${new Date(it.expires_at).toLocaleString()}` : "never expires"}</span>
                    <button onClick={() => renew(it, 24)} data-testid={`admin-ticker-renew-${it.id}`} className="rounded-sm border border-white/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]">Renew 24h</button>
                  </div>
                </div>
                <button
                  onClick={() => swapPriority(it)}
                  data-testid={`admin-ticker-priority-${it.id}`}
                  className="rounded-sm border border-white/15 px-2 py-1 font-oswald text-[10px] uppercase tracking-widest text-white hover:border-[#ffd60a] hover:text-[#ffd60a]"
                >
                  {it.priority === "urgent" ? "Downgrade" : "Escalate"}
                </button>
                <button
                  onClick={() => toggle(it)}
                  data-testid={`admin-ticker-toggle-${it.id}`}
                  role="switch"
                  aria-checked={it.enabled}
                  className={`relative h-6 w-11 rounded-full transition-colors ${it.enabled ? "bg-[#1f63c9]" : "bg-white/10"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${it.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30" aria-label="Up">
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="flex h-7 w-7 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#1f63c9] hover:text-[#1f63c9] disabled:opacity-30" aria-label="Down">
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
                <button
                  onClick={() => del(it.id)}
                  data-testid={`admin-ticker-delete-${it.id}`}
                  className="rounded-sm border border-white/15 p-2 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function SubscribersTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/subscribers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Remove this subscriber?")) return;
    try { await api.delete(`/subscribers/${id}`); await load(); toast.success("Removed"); }
    catch { toast.error("Failed to remove"); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/subscribers/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "gawx_subscribers.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const filtered = filter
    ? items.filter((s) => s.email.includes(filter.toLowerCase()) || (s.zip || "").includes(filter))
    : items;

  return (
    <div className="space-y-6">
      <div className="glass rounded-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">
              Newsletter Subscribers
            </div>
            <p className="mt-1 text-sm text-gray-400">
              Collected from the home-page CTA. Export to CSV to import into any mail service.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-xs text-white">
              {items.length.toLocaleString()} total
            </div>
            <Btn data-testid="admin-subs-export" onClick={exportCsv}>
              <Download className="h-4 w-4" /> Export CSV
            </Btn>
          </div>
        </div>

        <div className="mt-4">
          <Label>Filter by email or ZIP</Label>
          <Input
            data-testid="admin-subs-filter"
            placeholder="e.g. @gmail.com or 30303"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-4 font-oswald text-sm uppercase tracking-widest text-gray-400">
          Showing {filtered.length} of {items.length}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left font-oswald text-[11px] uppercase tracking-widest text-gray-400">
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">ZIP</th>
                <th className="py-2 pr-3">Joined</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-white/5 text-gray-200">
                  <td className="py-2 pr-3 font-mono text-xs">{s.email}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{s.zip || "—"}</td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-gray-400">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      data-testid={`admin-subs-delete-${s.id}`}
                      onClick={() => del(s.id)}
                      className="rounded-sm border border-white/15 p-1.5 text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-sm text-gray-400">
                    {items.length === 0 ? "No subscribers yet." : "No matches for that filter."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function TwitterTab() {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [accessTokenSecret, setAccessTokenSecret] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [autoWarn, setAutoWarn] = useState(true);
  const [autoSpc, setAutoSpc] = useState(true);
  const [minSev, setMinSev] = useState("all");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [posting, setPosting] = useState(false);
  const [testMsg, setTestMsg] = useState("Test tweet from Georgia Weather Network — connection verified. — GAWX");
  const [posts, setPosts] = useState([]);
  const [showSetup, setShowSetup] = useState(false);

  const reload = async () => {
    try {
      const [c, p] = await Promise.all([
        api.get("/twitter/config"),
        api.get("/twitter/posts?limit=25").catch(() => ({ data: { posts: [] } })),
      ]);
      setCfg(c.data);
      setEnabled(!!c.data.enabled);
      setAutoWarn(c.data.auto_post_warnings !== false);
      setAutoSpc(c.data.auto_post_spc !== false);
      setMinSev(c.data.min_severity || "all");
      setPosts(p.data.posts || []);
    } catch (e) { /* not admin */ }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled, auto_post_warnings: autoWarn, auto_post_spc: autoSpc, min_severity: minSev };
      if (apiKey) body.api_key = apiKey;
      if (apiSecret) body.api_secret = apiSecret;
      if (accessToken) body.access_token = accessToken;
      if (accessTokenSecret) body.access_token_secret = accessTokenSecret;
      const { data } = await api.put("/twitter/config", body);
      setCfg(data);
      setApiKey(""); setApiSecret(""); setAccessToken(""); setAccessTokenSecret("");
      toast.success("X / Twitter config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/twitter/test-connection");
      toast.success(`Connected as @${data.username} (${data.name})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const testPost = async () => {
    if (!testMsg.trim()) return toast.error("Enter a message");
    setPosting(true);
    try {
      const { data } = await api.post("/twitter/test-post", { message: testMsg });
      toast.success(`Tweeted: ${data.tweet_id}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tweet failed");
    } finally { setPosting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this X account? Auto-posting will stop.")) return;
    try {
      await api.put("/twitter/config", { clear_credentials: true, enabled: false, auto_post_warnings: autoWarn, auto_post_spc: autoSpc, min_severity: minSev });
      toast.success("X account disconnected");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
  };

  const charCount = testMsg.length;

  return (
    <div className="space-y-6" data-testid="twitter-tab">
      <div>
        <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1d9bf0]">Social Automation</div>
        <div className="font-oswald text-2xl font-bold uppercase tracking-widest text-white">X / Twitter Auto-Post</div>
        <p className="mt-1 max-w-2xl text-sm text-gray-400">
          Mirrors your Facebook auto-posts to X. NWS warnings are tweeted with the same 1200×900 polygon map image, tightened to fit 280 characters. SPC outlook posts are text-only.
        </p>
      </div>

      <button
        onClick={() => setShowSetup((v) => !v)}
        className="rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs uppercase tracking-widest text-white hover:border-[#1d9bf0]"
        data-testid="x-toggle-setup"
      >
        {showSetup ? "Hide" : "Show"} Setup Instructions
      </button>

      {showSetup && (
        <div className="glass rounded-sm p-5 text-sm text-gray-300 space-y-3">
          <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">One-Time X Developer Setup</div>
          <ol className="ml-4 list-decimal space-y-2">
            <li>Sign in to <a className="text-[#1d9bf0] underline" href="https://developer.x.com/en/portal/dashboard" target="_blank" rel="noreferrer">developer.x.com</a> with your GAWX Twitter account.</li>
            <li>The Free tier gives you 500 posts/month per user — plenty for severe weather. Click <b>Sign up for Free Account</b> and complete the developer application (a couple sentences about GAWX auto-posting warnings to your followers is fine).</li>
            <li>In the Portal, open <b>Projects & Apps → Default Project → your app</b>. Under <b>User authentication settings</b>, click <b>Set up</b>:
              <ul className="ml-6 mt-1 list-disc">
                <li>App permissions: <b>Read and write</b></li>
                <li>Type of App: <b>Web App, Automated App or Bot</b></li>
                <li>Callback URI: <code>https://peach-state-forecast.preview.emergentagent.com/admin</code> (any HTTPS URL works — we don't use it)</li>
                <li>Website URL: your website</li>
              </ul>
              Save.
            </li>
            <li>Go to <b>Keys and tokens</b>. Generate/regenerate:
              <ul className="ml-6 mt-1 list-disc">
                <li><b>API Key</b> & <b>API Key Secret</b> (also called Consumer Key/Secret)</li>
                <li><b>Access Token</b> & <b>Access Token Secret</b> (must be regenerated AFTER you set permissions to Read+Write — otherwise tweets will 403!)</li>
              </ul>
            </li>
            <li>Paste all 4 into the form below, click <b>Save Config</b>, then <b>Test Connection</b> — you should see your @handle appear. Then <b>Test Tweet</b> to verify posting works.</li>
            <li>Flip <b>Auto-Post Enabled</b> on — existing active warnings are marked as "already seen" so activating won't spam.</li>
          </ol>
          <div className="mt-2 rounded-sm bg-black/60 p-3 text-xs text-gray-400">
            <b className="text-white">Common pitfall:</b> If you get "403 Forbidden" on Test Tweet, it's almost always because you generated the Access Token BEFORE setting the app to Read+Write. Go back to Keys and tokens and hit <b>Regenerate</b> on both Access Token & Secret, then paste the new pair here.
          </div>
        </div>
      )}

      <div className="glass grid gap-4 rounded-sm p-5 md:grid-cols-2">
        <div>
          <Label>API Key (Consumer Key) {cfg?.api_key_masked && <span className="ml-2 text-[10px] normal-case text-emerald-400">current: {cfg.api_key_masked}</span>}</Label>
          <Input data-testid="x-api-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={cfg?.has_credentials ? "Leave blank to keep existing" : "Paste API Key"} />
        </div>
        <div>
          <Label>API Key Secret {cfg?.api_secret_masked && <span className="ml-2 text-[10px] normal-case text-emerald-400">current: {cfg.api_secret_masked}</span>}</Label>
          <Input data-testid="x-api-secret" type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} placeholder={cfg?.has_credentials ? "Leave blank to keep existing" : "Paste API Key Secret"} />
        </div>
        <div>
          <Label>Access Token {cfg?.access_token_masked && <span className="ml-2 text-[10px] normal-case text-emerald-400">current: {cfg.access_token_masked}</span>}</Label>
          <Input data-testid="x-access-token" type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder={cfg?.has_credentials ? "Leave blank to keep existing" : "Paste Access Token"} />
        </div>
        <div>
          <Label>Access Token Secret {cfg?.access_token_secret_masked && <span className="ml-2 text-[10px] normal-case text-emerald-400">current: {cfg.access_token_secret_masked}</span>}</Label>
          <Input data-testid="x-access-token-secret" type="password" value={accessTokenSecret} onChange={(e) => setAccessTokenSecret(e.target.value)} placeholder={cfg?.has_credentials ? "Leave blank to keep existing" : "Paste Access Token Secret"} />
        </div>

        <div className="md:col-span-2 grid gap-3 sm:grid-cols-3">
          <label className="flex items-center gap-3 rounded-sm border border-white/15 bg-black/40 px-3 py-2">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="x-enabled" />
            <span className="font-oswald text-xs uppercase tracking-widest text-white">Auto-Post Enabled</span>
          </label>
          <label className="flex items-center gap-3 rounded-sm border border-white/15 bg-black/40 px-3 py-2">
            <input type="checkbox" checked={autoWarn} onChange={(e) => setAutoWarn(e.target.checked)} data-testid="x-auto-warn" />
            <span className="font-oswald text-xs uppercase tracking-widest text-white">NWS Warnings</span>
          </label>
          <label className="flex items-center gap-3 rounded-sm border border-white/15 bg-black/40 px-3 py-2">
            <input type="checkbox" checked={autoSpc} onChange={(e) => setAutoSpc(e.target.checked)} data-testid="x-auto-spc" />
            <span className="font-oswald text-xs uppercase tracking-widest text-white">SPC Day 1 Outlook</span>
          </label>
        </div>

        <div>
          <Label>Minimum Severity to Tweet</Label>
          <select
            value={minSev}
            onChange={(e) => setMinSev(e.target.value)}
            className="w-full rounded-sm border border-white/15 bg-black/60 px-3 py-2 text-sm text-white outline-none focus:border-[#1d9bf0]"
            data-testid="x-min-severity"
          >
            <option value="all">All warnings/watches</option>
            <option value="moderate">Moderate and above</option>
            <option value="severe">Severe and above</option>
          </select>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Btn onClick={save} disabled={saving} data-testid="x-save" className="bg-[#1d9bf0] hover:bg-[#1a8cd8]">
            {saving ? "Saving…" : <><Check className="h-4 w-4" /> Save Config</>}
          </Btn>
          <button
            onClick={testConn}
            disabled={testing || (!cfg?.has_credentials && !(apiKey && apiSecret && accessToken && accessTokenSecret))}
            className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1d9bf0] disabled:opacity-40"
            data-testid="x-test-connection"
          >
            {testing ? "Testing…" : "Test Connection"}
          </button>
          {cfg?.has_credentials && (
            <button
              onClick={disconnect}
              className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-gray-300 hover:border-red-500 hover:text-red-400"
              data-testid="x-disconnect"
            >
              <Trash2 className="h-4 w-4" /> Disconnect
            </button>
          )}
        </div>
      </div>

      <div className="glass rounded-sm p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">Send a Test Tweet</div>
          <div className={`font-mono text-xs ${charCount > 280 ? "text-red-400" : "text-gray-400"}`}>{charCount} / 280</div>
        </div>
        <Textarea rows={3} value={testMsg} onChange={(e) => setTestMsg(e.target.value)} data-testid="x-test-message" />
        <Btn onClick={testPost} disabled={posting} data-testid="x-test-post" className="bg-[#1d9bf0] hover:bg-[#1a8cd8]">
          {posting ? "Tweeting…" : "🐦 Publish Test Tweet"}
        </Btn>
      </div>

      <div className="glass rounded-sm p-5">
        <div className="mb-3 font-oswald text-sm font-bold uppercase tracking-widest text-white">Tweet History (last 25)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-gray-400">
              <tr>
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Kind</th>
                <th className="py-2 pr-3">Text</th>
                <th className="py-2 pr-3">Result</th>
              </tr>
            </thead>
            <tbody data-testid="x-post-history">
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-white/10 align-top">
                  <td className="py-2 pr-3 font-mono text-xs text-gray-400 whitespace-nowrap">{p.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 pr-3 text-white uppercase text-xs">{p.kind}</td>
                  <td className="py-2 pr-3 text-gray-300"><div className="max-w-lg whitespace-pre-wrap">{p.message}</div></td>
                  <td className="py-2 pr-3">
                    {p.tweet_id ? (
                      <span className="text-emerald-400 text-xs font-mono">{p.tweet_id}</span>
                    ) : (
                      <span className="text-red-400 text-xs">{p.error || "failed"}</span>
                    )}
                  </td>
                </tr>
              ))}
              {posts.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-sm text-gray-500">No tweets yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function FacebookTab() {
  const [cfg, setCfg] = useState(null);
  const [pageId, setPageId] = useState("");
  const [token, setToken] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [autoWarn, setAutoWarn] = useState(true);
  const [autoSpc, setAutoSpc] = useState(true);
  const [minSev, setMinSev] = useState("all");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [posting, setPosting] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewWideUrl, setPreviewWideUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState(null); // null | []
  const [discoverMeta, setDiscoverMeta] = useState(null);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [testMsg, setTestMsg] = useState("Test post from Georgia Weather Network Admin — connection verified. — GAWX");
  const [posts, setPosts] = useState([]);
  const [showSetup, setShowSetup] = useState(false);

  const reload = async () => {
    try {
      const [c, p] = await Promise.all([
        api.get("/facebook/config"),
        api.get("/facebook/posts?limit=25").catch(() => ({ data: { posts: [] } })),
      ]);
      setCfg(c.data);
      setPageId(c.data.page_id || "");
      setAppId(c.data.app_id || "");
      setEnabled(!!c.data.enabled);
      setAutoWarn(c.data.auto_post_warnings !== false);
      setAutoSpc(c.data.auto_post_spc !== false);
      setMinSev(c.data.min_severity || "all");
      setPosts(p.data.posts || []);
    } catch (e) { /* not admin? */ }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { page_id: pageId, enabled, auto_post_warnings: autoWarn, auto_post_spc: autoSpc, min_severity: minSev, app_id: appId };
      if (token) body.page_access_token = token;
      if (appSecret) body.app_secret = appSecret;
      const { data } = await api.put("/facebook/config", body);
      setCfg(data);
      setToken("");
      setAppSecret("");
      toast.success("Facebook config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/facebook/test-connection");
      toast.success(`Connected to “${data.page_name}” (${data.page_id})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const testPost = async () => {
    if (!testMsg.trim()) return toast.error("Enter a message");
    setPosting(true);
    try {
      const { data } = await api.post("/facebook/test-post", { message: testMsg });
      toast.success(`Posted to Facebook: ${data.facebook_id}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    } finally { setPosting(false); }
  };

  const runNow = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/facebook/run-now");
      toast.success(`Sweep complete — ${data.new_posts} new post(s).`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally { setSweeping(false); }
  };

  const previewImage = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/facebook/preview-image");
      setPreviewUrl(data.url + "?ts=" + Date.now());
      setPreviewWideUrl(data.wide_url ? data.wide_url + "?ts=" + Date.now() : "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const discoverPages = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post("/facebook/discover-pages");
      setDiscovered(data.pages || []);
      setDiscoverMeta({ extended: data.extended, app_configured: data.app_configured });
      if (data.extended) toast.success("Token extended to 60 days — Page tokens from it never expire");
      if ((data.pages || []).length === 0) {
        toast.error("No Pages found. Your token may lack pages_manage_posts, or you're not a Page admin.");
      } else {
        toast.success(`Found ${data.pages.length} Page(s) — pick one below.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discover failed");
    } finally { setDiscovering(false); }
  };

  const selectPage = async (page) => {
    try {
      await api.post("/facebook/use-page", { page_id: page.id, page_access_token: page.page_access_token });
      toast.success(`Saved "${page.name}" as the target Page. Now click Test Connection.`);
      setDiscovered(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="facebook-tab">
      <div>
        <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">Social Automation</div>
        <div className="font-oswald text-2xl font-bold uppercase tracking-widest text-white">Facebook Auto-Post</div>
        <p className="mt-1 max-w-2xl text-sm text-gray-400">
          Auto-publishes to your Georgia Weather Network Facebook Page whenever a new NWS warning fires or the SPC updates the Day 1 Convective Outlook.
        </p>
      </div>

      <button
        onClick={() => setShowSetup((v) => !v)}
        className="rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs uppercase tracking-widest text-white hover:border-[#1f63c9]"
        data-testid="fb-toggle-setup"
      >
        {showSetup ? "Hide" : "Show"} Setup Instructions
      </button>

      {showSetup && (
        <div className="glass rounded-sm p-5 text-sm text-gray-300 space-y-3">
          <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">One-Time Facebook Developer Setup</div>
          <ol className="ml-4 list-decimal space-y-2">
            <li>Go to <a className="text-[#2a74d8] underline" href="https://developers.facebook.com/" target="_blank" rel="noreferrer">developers.facebook.com</a>, sign in with the account that manages the GAWX Facebook Page.</li>
            <li>Click <b>My Apps → Create App</b>. Choose <b>Other → Business</b>. Give it a name like “GAWX Auto-Poster” and enter your contact email.</li>
            <li>In the app dashboard, add the <b>Facebook Login for Business</b> product. Under <b>Settings → Basic</b>, save your <b>App ID</b> and <b>App Secret</b> (keep the secret private).</li>
            <li>Open <a className="text-[#2a74d8] underline" href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">Graph API Explorer</a>. Select your app.  Click <b>Get User Access Token</b> and grant these permissions:
              <ul className="ml-6 mt-1 list-disc">
                <li><code>pages_show_list</code></li>
                <li><code>pages_manage_posts</code></li>
                <li><code>pages_read_engagement</code></li>
              </ul>
            </li>
            <li>In the Explorer, run <code>GET /me/accounts</code>. Find the Georgia Weather Network Page in the response. Copy its <b>id</b> and <b>access_token</b> — that token is your long-lived Page Access Token (Page tokens generally don't expire).</li>
            <li>Paste the Page ID and Page Access Token below, click <b>Save</b>, then click <b>Test Connection</b>, then <b>Test Post</b> to verify.</li>
            <li>Toggle <b>Auto-Post Enabled</b> to ON — going forward, only NEW warnings/outlooks (fired after enabling) will publish. Existing active alerts are marked as “already seen” to avoid a burst of posts on activation.</li>
          </ol>
          <div className="mt-2 rounded-sm bg-black/60 p-3 text-xs text-gray-400">
            <b className="text-white">Note:</b> While your app is in Development Mode, only app testers/admins can see posts. To publish to the public Page, submit the app for App Review with <code>pages_manage_posts</code>, or promote the Page owner to an App Admin role.
          </div>
        </div>
      )}

      <div className="glass grid gap-4 rounded-sm p-5 md:grid-cols-2">
        <div>
          <Label>Facebook Page ID</Label>
          <Input data-testid="fb-page-id" value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="e.g. 123456789012345" />
        </div>
        <div>
          <Label>Page Access Token {cfg?.has_token && <span className="ml-2 text-[10px] normal-case text-emerald-400">current: {cfg?.page_access_token_masked}</span>}
            {cfg?.has_token && cfg?.token_expires_at === 0 && <span className="ml-2 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] normal-case text-emerald-300" data-testid="fb-token-never-expires">never expires</span>}
            {cfg?.has_token && cfg?.token_expires_at > 0 && <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] normal-case text-amber-300" data-testid="fb-token-expiry">expires {new Date(cfg.token_expires_at * 1000).toLocaleDateString()}</span>}
          </Label>
          <Input data-testid="fb-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={cfg?.has_token ? "Leave blank to keep existing token" : "Paste User Token from Graph Explorer (we'll extend it)"} />
        </div>
        <div className="md:col-span-2 rounded-sm border border-[#1877f2]/40 bg-[#1877f2]/5 p-3">
          <div className="font-oswald text-xs font-bold uppercase tracking-widest text-white">Extend Token automatically <span className="ml-2 text-[10px] normal-case text-gray-400">(Meta App ID + App Secret · from developers.facebook.com → your app → App settings → Basic)</span></div>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <Input data-testid="fb-app-id" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="App ID" />
            <Input data-testid="fb-app-secret" type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder={cfg?.has_app_secret ? "App Secret saved — leave blank to keep" : "App Secret"} />
          </div>
          <p className="mt-2 text-[11px] text-gray-400">With these saved, <b>Discover My Pages</b> first swaps your short-lived Explorer token for a 60-day token, so the Page token it hands back <b>never expires</b>.</p>
        </div>

        <div className="md:col-span-2 grid gap-3 sm:grid-cols-3">
          <label className="flex items-center gap-3 rounded-sm border border-white/15 bg-black/40 px-3 py-2">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="fb-enabled" />
            <span className="font-oswald text-xs uppercase tracking-widest text-white">Auto-Post Enabled</span>
          </label>
          <label className="flex items-center gap-3 rounded-sm border border-white/15 bg-black/40 px-3 py-2">
            <input type="checkbox" checked={autoWarn} onChange={(e) => setAutoWarn(e.target.checked)} data-testid="fb-auto-warn" />
            <span className="font-oswald text-xs uppercase tracking-widest text-white">NWS Warnings</span>
          </label>
          <label className="flex items-center gap-3 rounded-sm border border-white/15 bg-black/40 px-3 py-2">
            <input type="checkbox" checked={autoSpc} onChange={(e) => setAutoSpc(e.target.checked)} data-testid="fb-auto-spc" />
            <span className="font-oswald text-xs uppercase tracking-widest text-white">SPC Day 1 Outlook</span>
          </label>
        </div>

        <div>
          <Label>Minimum Severity to Post</Label>
          <select
            value={minSev}
            onChange={(e) => setMinSev(e.target.value)}
            className="w-full rounded-sm border border-white/15 bg-black/60 px-3 py-2 text-sm text-white outline-none focus:border-[#1f63c9]"
            data-testid="fb-min-severity"
          >
            <option value="all">All warnings/watches</option>
            <option value="moderate">Moderate and above</option>
            <option value="severe">Severe and above</option>
          </select>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Btn onClick={save} disabled={saving} data-testid="fb-save">
            {saving ? "Saving…" : <><Check className="h-4 w-4" /> Save Config</>}
          </Btn>
          <button
            onClick={testConn}
            disabled={testing || !cfg?.has_token && !token}
            className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] disabled:opacity-40"
            data-testid="fb-test-connection"
          >
            {testing ? "Testing…" : "Test Connection"}
          </button>
          {cfg?.has_token && (
            <button
              onClick={async () => {
                if (!window.confirm("Disconnect this Facebook Page? Auto-posting will stop until you paste a new token.")) return;
                try {
                  await api.put("/facebook/config", { clear_token: true, enabled: false, auto_post_warnings: autoWarn, auto_post_spc: autoSpc, min_severity: minSev, page_id: pageId });
                  toast.success("Page disconnected");
                  reload();
                } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
              }}
              className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-gray-300 hover:border-red-500 hover:text-red-400"
              data-testid="fb-disconnect"
            >
              <Trash2 className="h-4 w-4" /> Disconnect
            </button>
          )}
        </div>
      </div>

      {cfg?.has_token && (
        <div className="glass rounded-sm p-5 space-y-3" data-testid="fb-discover-block">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">Not sure which Page ID to use?</div>
              <p className="text-xs text-gray-400 mt-1">Click below and we'll ask Facebook which Pages your token can manage. Pick one and we'll auto-fill the correct Page ID + long-lived Page Access Token.</p>
            </div>
            <button
              onClick={discoverPages}
              disabled={discovering}
              className="inline-flex items-center gap-2 rounded-sm border border-[#1877f2] bg-[#1877f2]/10 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:bg-[#1877f2]/20 disabled:opacity-40"
              data-testid="fb-discover"
            >
              {discovering ? "Discovering…" : "🔍 Discover My Pages"}
            </button>
          </div>
          {discovered && discovered.length > 0 && (
            <div className="mt-3 divide-y divide-white/10 rounded-sm border border-white/15" data-testid="fb-discovered-list">
              {discovered.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div>
                    <div className="text-sm font-bold text-white">{p.name}</div>
                    <div className="mt-1 font-mono text-xs text-gray-400">
                      id: {p.id} · {p.category || "Uncategorized"} · {p.can_create_content ? <span className="text-emerald-400">✓ Can post</span> : <span className="text-red-400">✗ No posting permission</span>}
                      {p.expires_at === 0 && <span className="ml-2 text-emerald-300">· token never expires</span>}
                      {p.expires_at > 0 && <span className="ml-2 text-amber-300">· token expires {new Date(p.expires_at * 1000).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => selectPage(p)}
                    disabled={!p.can_create_content}
                    className="rounded-sm bg-[#1f63c9] px-3 py-1.5 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:bg-[#b8031f] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`fb-use-page-${p.id}`}
                  >
                    Use this Page
                  </button>
                </div>
              ))}
            </div>
          )}
          {discoverMeta && !discoverMeta.app_configured && (
            <div className="mt-3 rounded-sm bg-amber-950/40 p-3 text-xs text-amber-200" data-testid="fb-extend-hint">
              Tip: add your Meta App ID + App Secret above and Save, then Discover again — we'll extend the token so it never expires.
            </div>
          )}
          {discovered && discovered.length === 0 && (
            <div className="mt-3 rounded-sm bg-red-950/40 p-3 text-sm text-red-300">
              No Pages returned. Your token likely lacks the <code>pages_manage_posts</code> permission, or you're not an admin of any Facebook Page. Go back to Graph API Explorer, add <code>pages_manage_posts</code> under a Use Case, regenerate the token, and paste it here.
            </div>
          )}
        </div>
      )}

      <ImpactPostCard />

      <div className="glass rounded-sm p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">Warning Image Preview</div>
            <p className="text-xs text-gray-400">Every NWS warning auto-post attaches a broadcast-style graphic: zoomed street map + live radar + polygon + who's impacted. Facebook gets the <span className="text-white">1080×1350 portrait</span>; X gets portrait + the <span className="text-white">16:9 on-air</span> version. SPC outlook posts attach the official SPC Day 1 graphic.</p>
          </div>
          <button
            onClick={previewImage}
            disabled={previewing}
            className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] disabled:opacity-40"
            data-testid="fb-preview-image"
          >
            {previewing ? "Rendering…" : "Render Sample Image"}
          </button>
        </div>
        {previewUrl && (
          <div className="grid gap-4 md:grid-cols-[2fr_3fr]" data-testid="fb-preview-grid">
            <div>
              <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-gray-400">Portrait · 1080×1350 · Facebook + X</div>
              <img src={previewUrl} alt="Portrait social graphic" className="w-full rounded-sm border border-white/15" data-testid="fb-preview-img" />
            </div>
            {previewWideUrl && (
              <div>
                <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-gray-400">Wide · 1920×1080 · On-air cut-in + X</div>
                <img src={previewWideUrl} alt="Wide broadcast graphic" className="w-full rounded-sm border border-white/15" data-testid="fb-preview-img-wide" />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="glass rounded-sm p-5 space-y-3">
        <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">Send a Test Post</div>
        <Textarea rows={3} value={testMsg} onChange={(e) => setTestMsg(e.target.value)} data-testid="fb-test-message" />
        <div className="flex flex-wrap gap-2">
          <Btn onClick={testPost} disabled={posting} data-testid="fb-test-post">
            {posting ? "Posting…" : <>📢 Publish Test Post</>}
          </Btn>
          <button
            onClick={runNow}
            disabled={sweeping || !enabled}
            className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] disabled:opacity-40"
            data-testid="fb-run-now"
            title={!enabled ? "Enable auto-post first" : "Force an immediate NWS + SPC sweep"}
          >
            {sweeping ? "Sweeping…" : "Force Sweep Now"}
          </button>
        </div>
      </div>

      <div className="glass rounded-sm p-5">
        <div className="mb-3 font-oswald text-sm font-bold uppercase tracking-widest text-white">Post History (last 25)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-gray-400">
              <tr>
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Kind</th>
                <th className="py-2 pr-3">Message</th>
                <th className="py-2 pr-3">Result</th>
              </tr>
            </thead>
            <tbody data-testid="fb-post-history">
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-white/10 align-top">
                  <td className="py-2 pr-3 font-mono text-xs text-gray-400 whitespace-nowrap">{p.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 pr-3 text-white uppercase text-xs">{p.kind}</td>
                  <td className="py-2 pr-3 text-gray-300"><div className="max-w-lg whitespace-pre-wrap">{p.message}</div></td>
                  <td className="py-2 pr-3">
                    {p.facebook_id ? (
                      <span className="text-emerald-400 text-xs font-mono">{p.facebook_id}</span>
                    ) : (
                      <span className="text-red-400 text-xs">{p.error || "failed"}</span>
                    )}
                  </td>
                </tr>
              ))}
              {posts.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-sm text-gray-500">No posts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const initialTab = (() => {
    if (typeof window === "undefined") return "stream";
    const p = new URLSearchParams(window.location.search).get("tab");
    return p && TABS.some((t) => t.id === p) ? p : "stream";
  })();
  const [tab, setTab] = useState(initialTab);
  const nav = useNavigate();

  if (!ready) return <div className="flex min-h-screen items-center justify-center text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10 bg-[#0a1222]/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div>
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">Control Room</div>
            <div className="font-oswald text-xl font-bold uppercase tracking-widest text-white">GAWX Admin</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden font-mono text-xs text-gray-400 sm:block">{user.email}</span>
            <button
              data-testid={ADMIN.logout}
              onClick={async () => { await logout(); nav("/"); }}
              className="flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-6 md:grid-cols-[220px_1fr]">
        <aside className="glass rounded-sm p-3 md:sticky md:top-6 md:h-fit">
          <nav className="flex md:flex-col overflow-x-auto scrollbar-hide">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={t.testId}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-3 rounded-sm px-3 py-2 text-left font-oswald text-sm uppercase tracking-widest whitespace-nowrap ${
                    active
                      ? "bg-[#1f63c9] text-white"
                      : "text-gray-300 hover:bg-white/5"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main>
          {tab === "stream" && <StreamTab />}
          {tab === "bumper" && <BumperTab />}
          {tab === "streaming-setup" && <StreamingSetupTab />}
          {tab === "slides" && <SlidesTab />}
          {tab === "cameras" && <CamerasTab />}
          {tab === "videos" && <VideosTab />}
          {tab === "studio" && <ForecastStudio />}
          {tab === "articles" && <ArticlesTab />}
          {tab === "history" && <HistoryTab />}
          {tab === "alerts" && <AlertsTab />}
          {tab === "ticker" && <TickerTab />}
          {tab === "facebook" && <FacebookTab />}
          {tab === "twitter" && <TwitterTab />}
          {tab === "share" && <ShareGraphicsTab />}
          {tab === "cutin" && <CutInTab />}
          {tab === "yt-meta" && <YouTubeMetaCard />}
          {tab === "social-studio" && <SocialStudioCard />}
          {tab === "afd-articles" && <AfdArticlesCard />}
          {tab === "team" && <TeamTab />}
          {tab === "sponsors" && <SponsorsTab />}
          {tab === "subscribers" && <SubscribersTab />}
        </main>
      </div>
    </div>
  );
}
